import React, { useEffect, useState } from "react";
import InputDropdown from "./formElements/InputDropdown";
import useETFormState from "./formElements/useETFormState";
import InputText from "./formElements/InputText";
import InputTextarea from "./formElements/InputTextarea";
import {
  additionalFields,
  skillFields,
  prefilledForm,
  notMandatoryFields,
} from "./formElements/additionalFields";
import axios from "axios";
import { config as axiosConfig } from "../shared/config";
import CheckboxInputDropdown from "./formElements/CheckboxInputDropdown";
import { useRouter } from "next/router";
import { toast } from "react-toastify";

const MAPPING = {
  family: "domain",
  product: "family",
  area: "leadPlatform",
  subArea: "area",
  mco: "cluster",
  country: "mco",
};

const FIELD_RESET_MAPPING = {
  leadPlatform: ["area", "subArea"],
  area: ["subArea"],
  cluster: ["mco", "country"],
  mco: ["country"],
};

export const updateDropdownList = (item, formData) => {
  if (
    item.value === "domain" ||
    item.value === "leadPlatform" ||
    item.value === "cluster" ||
    item.value === "engagementLead" ||
    item.value === "status"
  ) {
    return item.field_options;
  } else if (item.value === "country") {
    //
    const getCountryArr = formData[MAPPING["country"]]?.split(",");

    const getList = getCountryArr.map((country) => {
      const getMCO = item.field_dropdown_option_component.find(
        (listItem) => listItem.field_title === country
      );
      return getMCO?.field_options || [];
    });

    return getList.flat() || [];
  } else {
    const findList = item.field_dropdown_option_component.find(
      (listItem) => listItem.field_title === formData[MAPPING[item.value]]
    );

    return findList?.field_options || [];
  }
};

const CreateETForm = ({
  closeETForm,
  globalData,
  formDataBots,
  setNewlyCreatedET,
  state,
}) => {
  const CLUSTER = globalData.cluster_main_component;
  const LEAD_PLATFORM = globalData.lead_platform_main_component;
  const ENGAGEMENT_LEAD = formDataBots.find(
    (item) => item.field_label === "Engagement Lead"
  );
  const STATUS = {
    field_label: "Status",
    field_input: "Select status",
    value: "status",
    field_options: ["Live", "Build"],
  };

  const { formData, updateForm, clearForm } = useETFormState();

  const [searchedSkill, setSearchedSkill] = useState(null);
  const [error, setError] = useState("");

  const router = useRouter();

  const searchSkills = async (name, value) => {
    if (value.length < 2) return;

    const API =
      name === "softSkill"
        ? `https://automation.unilever.com/api/softSkill/get?softSkillID=${value}`
        : `https://automation.unilever.com/api/botSearch/globalLocal?botID=${value}`;

    let auth;

    if (typeof window !== "undefined") {
      auth = JSON.parse(localStorage.getItem("auth")).data;
    }

    const config = {
      headers: {
        Authorization: auth.accessToken,
      },
    };

    try {
      const res = await axios.get(API, config);

      if (res.data.message === "Error while authentication") {
        setError("Session Expired - Moving to Home");
        setTimeout(() => {
          closeETForm();
          router.push("/");
        }, [700]);
        return;
      }

      const data = res.data.data?.[0];

      const formatData =
        name === "softSkill"
          ? { id: data.softSkillID, name: data.skillName, type: name }
          : { id: data.botExternalId, name: data.processName, type: name };

      setSearchedSkill(formatData);
    } catch (error) {
      console.log("Error", error);
    }
  };

  const updateSkills = (name, value) => {
    if (formData[name].includes(value)) {
      return;
    }

    if (!formData[name]) {
      updateForm(name, value);
    } else {
      updateForm(name, `${formData[name]},${value}`);
    }
    // setSearchedSkill(null);
  };

  const submitETForm = async () => {
    console.log("formData", formData);

    //check for empty fields
    for (let key of Object.keys(formData).filter(
      (key) => !notMandatoryFields.includes(key)
    )) {
      if (!formData[key]) {
        setError(`${key} is a required field`);
        return;
      }
    }

    let auth;
    if (typeof window !== "undefined") {
      auth = JSON.parse(localStorage.getItem("auth")).data;
    }

    const config = {
      headers: {
        Authorization: auth.accessToken,
      },
    };

    try {
      const getLatestET = await axios.get(
        `${axiosConfig.baseUrl}/bot/get-latest-et`,
        config
      );

      if (getLatestET.data.message === "Error while authentication") {
        setError("Session Expired - Relogin");
        return;
      }

      const nextET =
        Number(getLatestET.data.data.employeeTwinID?.split("T")[1]) + 1;

      const getLength = `${nextET}`.length;

      const employeeTwinID =
        getLength <= 2 ? `ET000${nextET}` : `ET00${nextET}`;

      const payload = {
        employeeTwinID,
        ...formData,
        ...prefilledForm,
      };

      const res = await axios.post(
        `${axiosConfig.baseUrl}/bot/create-et`,
        payload,
        config
      );

      const newET = res.data.data.employeeTwinName;

      if (newET) {
        setNewlyCreatedET(newET);
        toast.success(`${newET} created`);
      }

      console.log("newET", newET);

      clearForm();
    } catch (error) {
      console.log("Error", error);
    }
  };

  useEffect(() => {
    for (let key of Object.keys(state)) {
      if (state[key]) {
        updateForm(key, state[key]);
      }
    }
  }, []);

  useEffect(() => {
    setError("");
  }, [formData]);

  return (
    <div className="fixed z-[9999] shadow left-[50%] translate-x-[-50%] top-28 w-[95%] h-[70vh] max-h-[70vh] overflow-y-auto bg-white">
      <div className="w-[90%] mx-auto h-full py-3">
        <div className="text-center font-unilever-shilling-medium text-[#1F36C7] text-lg">
          Create ET Profile
        </div>
        <div className="grid grid-cols-3 gap-5 mt-3">
          <div className="col-span-3 grid grid-cols-3">
            <InputText
              label={"Employee Twin Name"}
              placeholder={"Enter employee description"}
              name={"employeeTwinName"}
              updateForm={updateForm}
              value={formData["employeeTwinName"]}
            />
          </div>
          <InputTextarea
            label="Employee Twin Description"
            placeholder={"Enter employee twin name"}
            name={"employeeTwinDescription"}
            updateForm={updateForm}
            value={formData["employeeTwinDescription"]}
          />
          {[...LEAD_PLATFORM, ...CLUSTER, ENGAGEMENT_LEAD, STATUS].map(
            (item, idx) => (
              <>
                {item.value === "mco" || item.value === "country" ? (
                  <CheckboxInputDropdown
                    key={idx}
                    label={item.field_label}
                    placeholder={item.field_input}
                    list={updateDropdownList(item, formData)}
                    name={item.value}
                    updateForm={updateForm}
                    value={formData[item.value]}
                  />
                ) : (
                  <InputDropdown
                    key={idx}
                    label={item.field_label}
                    placeholder={item.field_input}
                    list={updateDropdownList(item, formData)}
                    name={item.value}
                    updateForm={updateForm}
                    value={formData[item.value]}
                  />
                )}
              </>
            )
          )}
          {additionalFields.map((field, idx) => (
            <InputText
              key={idx}
              label={field.label}
              placeholder={field.placeholder}
              name={field.name}
              inputType={field.inputType}
              updateForm={updateForm}
              value={formData[field.value]}
            />
          ))}
          {skillFields.map((field, idx) => (
            <InputText
              key={idx}
              label={field.label}
              placeholder={field.placeholder}
              name={field.name}
              updateForm={searchSkills}
              //value={formData[field.value]}
              searchedSkill={searchedSkill}
              updateSkills={updateSkills}
              formData={formData}
            />
          ))}
        </div>
        <div className="flex flex-col justify-center py-4">
          {error && (
            <p className="bg-red-500 capitalize w-[520px] mx-auto text-white py-2 text-center text-xs font-unilever-shilling-normal">
              {error}
            </p>
          )}
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={closeETForm}
              className="font-unilever-shilling-normal text-sm  !border !border-[#1f36c7] py-2 text-center min-w-[250px] text-[#1f36c7]"
            >
              Cancel
            </button>
            <button
              onClick={submitETForm}
              className="font-unilever-shilling-normal text-sm bg-[#1f36c7] py-2 text-center min-w-[250px] text-white"
            >
              Create ET {formData.employeeTwinName}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateETForm;

